import React, { useState } from 'react';
import { Popover, MenuList, Drawer } from 'aha-components';
import Avatar from './Avatar';
import {
  CreditScoreIcon,
  ExitIcon,
  HelpCenterIcon,
  RightArrowIcon,
  SettingsIcon,
  SupportIcon,
  WebsiteIcon
} from '../common/icon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SignOutButton } from '@clerk/clerk-react';
import { getClerkSignOutRedirectUrl } from '@/biz/tool';
import { langList } from '@/i18n';
import { handleI18nChange } from '@/i18n/utils';

export default function AvatarPanel({
  setMenuOpen,
  compact
}: {
  setMenuOpen?: (open: boolean) => void;
  /** 收起侧栏时仅展示圆形头像，宽度与 48px 栏匹配 */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showLanguageDrawer, setShowLanguageDrawer] = useState(false);
  const closePopover = () => setOpen(false);
  const items = [
    {
      children: [
        {
          icon: <SettingsIcon className="w-[20px] h-[20px] text-[#A9A29D]" />,
          key: 'settings',
          label: t('navbar.menu.settings', { defaultValue: 'Settings' }),
          onClick: () => {
            navigate('/settings');
            closePopover();
          }
        },
        {
          key: 'language',
          render: () => (
            <div className="flex items-center cursor-pointer gap-3 py-3 px-4">
              <WebsiteIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
              <Popover
                className="w-full"
                content={
                  <ul className="px-[8px] py-[16px]">
                    {langList.map((item) => (
                      <li
                        key={item.detectedValue}
                        onClick={() => handleI18nChange(item.detectedValue)}
                        className="px-[16px] py-[8px] rounded-[16px] hover:bg-[#F9F9FB] cursor-pointer"
                      >
                        <span className="text-sm font-medium font-['Inter'] leading-[22px]">
                          {item.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                }
                placement="right"
                trigger="click"
                width={264}
              >
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5 flex items-center justify-between w-full">
                  {t('settings.menu.language', { defaultValue: 'Language' })}
                  <RightArrowIcon className="w-5 h-5 text-[#79716B]" />
                </div>
              </Popover>
            </div>
          )
        },
        {
          icon: <HelpCenterIcon className="w-[20px] h-[20px] text-[#A9A29D]" />,
          key: 'help-center',
          label: t('navbar.help.text', { defaultValue: 'Help Center' }),
          onClick: () => {
            window.open(
              'https://www.ahacreator.com/creators/resources/help-center',
              '_blank'
            );
            closePopover();
          }
        },
        {
          icon: <SupportIcon className="w-[20px] h-[20px] text-[#A9A29D]" />,
          key: 'support',
          label: t('navbar.menu.support', { defaultValue: 'Support' }),
          onClick: () => {
            window.open('mailto:support@creator.aha.inc', '_blank');
            closePopover();
          }
        },
        {
          icon: (
            <CreditScoreIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
          ),
          key: 'support',
          label: t('navbar.menu.credit_score', {
            defaultValue: 'Rating and Review'
          }),
          onClick: () => {
            navigate('/reviews');
            closePopover();
          }
        }
      ]
    },
    {
      children: [
        {
          key: 'logout',
          render: () => (
            <SignOutButton redirectUrl={getClerkSignOutRedirectUrl()}>
              <div className="flex items-center cursor-pointer gap-3 py-3 px-4">
                <ExitIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                  {t('navbar.menu.log_out', { defaultValue: 'Sign out' })}
                </div>
              </div>
            </SignOutButton>
          )
        }
      ]
    }
  ];

  return (
    <>
      <Popover
        open={open}
        onOpenChange={setOpen}
        content={<MenuList items={items as any} width={264} />}
        placement="right"
        trigger="click"
        width={264}
        className="!hidden md:!block"
      >
        {compact ? (
          <span className="inline-flex border-0 shadow-none ring-0 outline-none bg-transparent p-0">
            <Avatar compact />
          </span>
        ) : (
          <Avatar />
        )}
      </Popover>
      <div className="md:hidden">
        <div onClick={() => setIsDrawerOpen(true)}>
          <Avatar compact={compact} />
        </div>
        <Drawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          zIndex={1400}
          height={352}
          placement="bottom"
          closable={false}
          drawerStyle={{ borderRadius: '16px 16px 0 0' }}
          bodyStyle={{ padding: 0 }}
        >
          <div className="flex flex-col gap-3 py-4">
            <div className="px-4 flex flex-col gap-3">
              <div
                className="flex items-center cursor-pointer gap-3 px-[14px] h-[36px]"
                onClick={() => {
                  navigate('/settings');
                  setMenuOpen?.(false);
                  setIsDrawerOpen(false);
                }}
              >
                <SettingsIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                  {t('navbar.menu.settings', { defaultValue: 'Settings' })}
                </div>
              </div>
              <div
                className="flex items-center cursor-pointer gap-3 px-[14px] h-[36px]"
                onClick={() => {
                  setShowLanguageDrawer(true);
                }}
              >
                <WebsiteIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5 flex items-center justify-between w-full">
                  {t('settings.menu.language', { defaultValue: 'Language' })}
                  <RightArrowIcon className="w-5 h-5 text-[#79716B]" />
                </div>
              </div>
              <div
                className="flex items-center cursor-pointer gap-3 px-[14px] h-[36px]"
                onClick={() => {
                  window.open(
                    'https://www.ahacreator.com/creators/resources/help-center',
                    '_blank'
                  );
                  setIsDrawerOpen(false);
                }}
              >
                <HelpCenterIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                  {t('navbar.help.text', { defaultValue: 'Help Center' })}
                </div>
              </div>
              <div
                className="flex items-center cursor-pointer gap-3 px-[14px] h-[36px]"
                onClick={() => {
                  window.open('mailto:support@creator.aha.inc', '_blank');
                  setIsDrawerOpen(false);
                }}
              >
                <SupportIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                  {t('navbar.menu.support', { defaultValue: 'Support' })}
                </div>
              </div>
              <div
                className="flex items-center cursor-pointer gap-3 px-[14px] h-[36px]"
                onClick={() => {
                  navigate('/reviews');
                  setIsDrawerOpen(false);
                  setIsDrawerOpen(false);
                }}
              >
                <CreditScoreIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
                <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                  {t('navbar.menu.credit_score', {
                    defaultValue: 'Rating and Review'
                  })}
                </div>
              </div>
            </div>
            <div className="border-t border-borderColor"></div>
            <div className="flex items-center cursor-pointer gap-3 py-3 px-[30px]">
              <ExitIcon className="w-[20px] h-[20px] text-[#A9A29D]" />
              <div className=" text-[#44403C] text-sm font-semibold font-['DM_Sans'] leading-5">
                {t('navbar.menu.log_out', { defaultValue: 'Sign out' })}
              </div>
            </div>
          </div>
        </Drawer>
        <Drawer
          open={showLanguageDrawer}
          onClose={() => setShowLanguageDrawer(false)}
          zIndex={1401}
          placement="bottom"
          closable={false}
          drawerStyle={{ borderRadius: '16px 16px 0 0' }}
          bodyStyle={{ padding: 0 }}
        >
          <ul className="px-[8px] py-[16px]">
            {langList.map((item) => (
              <li
                key={item.detectedValue}
                onClick={() => handleI18nChange(item.detectedValue)}
                className="px-[16px] py-[8px] rounded-[16px] hover:bg-[#F9F9FB] cursor-pointer"
              >
                <span className="text-sm font-medium font-['Inter'] leading-[22px]">
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
        </Drawer>
      </div>
    </>
  );
}
